// Ingredient detail/editor: aisle, "not suitable for" diets, synonyms, nutrition.
// Every ingredient must declare the diets it breaks: the user's own ones ask until confirmed.
// Edit mode changes nothing in the layout: texts become fields in place, nutrition opens a sheet.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { type DetailParams } from '../navigation';
import { useHeader } from '../hooks';
import { dietsDeclared } from '../logic/diets';
import { font, radius, spacing, usePalette } from '../theme';
import { useAllIngredients, useLibrary } from '../store/library';
import { MICRONUTRIENTS } from '../logic/nutrition';
import { View } from 'react-native';
import { Body, Button, Caption, Chip, EmptyState, Field, IconButton, Row, Screen, Section } from '../components/ui';
import { EditableText, Fade } from '../components/Motion';
import Sheet from '../components/Sheet';
import { DIETS, INGREDIENT_AISLES, type Ingredient, type Nutrition } from '../types';
import { CONTENT_RECIPES } from '../../content';

const MACROS = ['calories', 'protein', 'fat', 'carbohydrates', 'sugar', 'fiber'] as const;

export default function IngredientScreen({ route }: NativeStackScreenProps<DetailParams, 'Ingredient'>) {
  const { t } = useTranslation();
  const p = usePalette();
  const all = useAllIngredients();
  const put = useLibrary(s => s.put);
  const recipes = useLibrary(s => s.recipes);
  const item = all[route.params.id];
  const [editing, setEditing] = useState(!!route.params.edit);
  const [nutritionSheet, setNutritionSheet] = useState(false);
  const synonyms = item?.synonyms.join(', ') ?? '';
  // Typed text is kept as is while editing ("salt, " would otherwise lose its comma mid-word).
  const [synonymText, setSynonymText] = useState(synonyms);
  useEffect(() => {
    if (!editing) setSynonymText(synonyms);
  }, [editing, synonyms]);
  useHeader(
    { title: item?.title ?? '', headerRight: () => <IconButton icon={editing ? 'check' : 'edit'} label={editing ? t('common.done') : t('common.edit')} onPress={() => setEditing(e => !e)} /> },
    [editing, item?.title, t],
  );
  if (!item) return <EmptyState icon="ingredient" title={t('common.empty')} />;

  const update = (patch: Partial<Ingredient>) => put('ingredients', { ...item, ...patch });
  const setNutrient = (key: string, text: string) => {
    const n = text.trim() === '' ? undefined : Math.round(Number(text.replace(',', '.')));
    const values = { ...(item.nutrition?.values ?? {}) };
    if (n === undefined || Number.isNaN(n)) delete values[key];
    else values[key] = n;
    update({ nutrition: { basis: item.nutrition?.basis ?? 'per100Grams', values } });
  };
  const declared = dietsDeclared(item);
  const usedIn = [...Object.values(recipes), ...Object.values(CONTENT_RECIPES)].filter(r => r.ingredients.some(l => l.ingredientId === item.id)).length;
  const nutrients = [...MACROS.map(k => ({ key: k as string, title: t(`nutrition.${k}`) })), ...MICRONUTRIENTS.map(m => ({ key: m.key, title: m.title }))];
  const unitOf = (key: string) => (key === 'calories' ? 'kcal' : 'mg');

  return (
    <Screen>
      <EditableText editing={editing} value={item.title} onChangeText={v => update({ title: v.replace(/\n/g, ' ') })} accessibilityLabel={t('editors.title')}
        style={{ fontSize: font.title, fontWeight: '700', color: p.text, lineHeight: 34 }} />
      {item.builtIn ? <Fade visible={editing}><Caption>{t('editors.builtIn')}</Caption></Fade> : null}
      <Caption>{t('editors.usedInRecipes', { count: usedIn })}</Caption>
      <Section title={t('editors.aisle')}>
        <Row>
          {INGREDIENT_AISLES.map(a => (
            <Chip key={a} label={t(`aisles.${a}`)} selected={item.aisle === a} onPress={editing ? () => update({ aisle: a }) : undefined} />
          ))}
        </Row>
      </Section>
      {declared ? (
        <Section title={t('editors.dietRestrictions')}>
          <Row>
            {DIETS.map(d => {
              const on = item.notDiet.includes(d);
              return <Chip key={d} label={t(`diets.${d}`)} icon={on ? 'close' : undefined} selected={on} onPress={editing ? () => update({ notDiet: on ? item.notDiet.filter(x => x !== d) : [...item.notDiet, d] }) : undefined} />;
            })}
            {!item.notDiet.length ? <Caption>{t('diet.breaksNone')}</Caption> : null}
          </Row>
        </Section>
      ) : (
        <View style={{ gap: spacing.m, padding: spacing.l, borderRadius: radius.l, borderWidth: 1, borderColor: p.warning }}>
          <Section title={t('diet.declareTitle')} icon="warning">
            <Body secondary>{t('diet.declareBody')}</Body>
          </Section>
          <Row>
            {DIETS.map(d => {
              const on = item.notDiet.includes(d);
              return <Chip key={d} label={t(`diets.${d}`)} icon={on ? 'close' : undefined} selected={on} onPress={() => update({ notDiet: on ? item.notDiet.filter(x => x !== d) : [...item.notDiet, d] })} />;
            })}
          </Row>
          <Button label={item.notDiet.length ? t('diet.confirm') : t('diet.confirmNone')} icon="check" onPress={() => update({ dietsChecked: true })} />
        </View>
      )}
      <Section title={t('editors.synonyms')}>
        <EditableText
          editing={editing}
          value={editing ? synonymText : synonyms || '—'}
          onChangeText={v => {
            setSynonymText(v);
            update({ synonyms: v.split(/[,\n]/).map(s => s.trim()).filter(Boolean) });
          }}
          placeholder="—"
          style={{ fontSize: font.body, color: p.secondaryText, lineHeight: 22 }}
        />
      </Section>
      <Section title={t('common.about')}>
        <EditableText editing={editing} value={item.explainer} onChangeText={v => update({ explainer: v })} placeholder={t('common.notes')} style={{ fontSize: font.body, color: p.text, lineHeight: 22 }} />
      </Section>
      <Section
        title={t('nutrition.editor')}
        icon="nutrition"
        right={<Fade visible={editing}><IconButton icon="edit" label={t('nutrition.editor')} onPress={() => setNutritionSheet(true)} /></Fade>}>
        {item.nutrition ? (
          <>
            <Caption>{t('nutrition.basis')}</Caption>
            <Row>
              <Chip label={t('nutrition.per100g')} selected={item.nutrition.basis === 'per100Grams'} onPress={editing ? () => update({ nutrition: { ...item.nutrition!, basis: 'per100Grams' } }) : undefined} />
              <Chip label={t('nutrition.perItem')} selected={item.nutrition.basis === 'perItem'} onPress={editing ? () => update({ nutrition: { ...item.nutrition!, basis: 'perItem' } }) : undefined} />
            </Row>
            {nutrients.map(n => {
              const value = item.nutrition!.values[n.key];
              return value == null ? null : <Caption key={n.key}>{n.title}: {value} {unitOf(n.key)}</Caption>;
            })}
          </>
        ) : (
          <Caption>{t('nutrition.noMacros')}</Caption>
        )}
      </Section>
      <Sheet visible={nutritionSheet} onClose={() => setNutritionSheet(false)} title={t('nutrition.editor')}>
        {item.nutrition ? (
          <Row>
            <Chip label={t('nutrition.per100g')} selected={item.nutrition.basis === 'per100Grams'} onPress={() => update({ nutrition: { ...item.nutrition!, basis: 'per100Grams' } })} />
            <Chip label={t('nutrition.perItem')} selected={item.nutrition.basis === 'perItem'} onPress={() => update({ nutrition: { ...item.nutrition!, basis: 'perItem' } })} />
          </Row>
        ) : (
          <Button label={t('nutrition.createProfile')} icon="plus" variant="secondary" onPress={() => update({ nutrition: { basis: 'per100Grams', values: {} } satisfies Nutrition })} />
        )}
        {nutrients.map(n => {
          const value = item.nutrition?.values[n.key];
          return <Field key={n.key} label={`${n.title} (${unitOf(n.key)})`} value={value == null ? '' : String(value)} keyboardType="number-pad" onChangeText={v => setNutrient(n.key, v)} />;
        })}
      </Sheet>
    </Screen>
  );
}
